import React from "react";
import { GlassCard } from "../components/GlassCard";
import { useBudget } from "../context/BudgetContext";
import { useI18n } from "../context/I18nContext";
import { AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";
import { cn } from "../lib/utils";

const LEVEL_STYLE = {
  alerte: {
    bg: "bg-rose-50",
    border: "border-rose-200",
    text: "text-rose-700",
    icon: AlertTriangle,
  },
  opportunite: {
    bg: "bg-indigo-50",
    border: "border-indigo-200",
    text: "text-indigo-700",
    icon: Sparkles,
  },
  info: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-700",
    icon: CheckCircle2,
  },
};

const RecoCard = ({ reco, t }) => {
  const style = LEVEL_STYLE[reco.level];
  const Icon = style.icon;
  return (
    <div
      data-testid={`reco-${reco.id}`}
      className={cn(
        "flex items-start gap-4 p-5 rounded-xl border transition-all hover:shadow-sm",
        style.bg,
        style.border
      )}
    >
      <div className={cn("w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0 shadow-sm", style.text)}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className={cn("text-sm font-medium", style.text)}>
            {t.recommandations.labels[reco.labelKey]}
          </span>
          <span className={cn("text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-full", style.bg, style.text, "border", style.border)}>
            {reco.level === "alerte"
              ? t.recommandations.alertes
              : reco.level === "opportunite"
              ? t.recommandations.opportunites
              : t.recommandations.infos}
          </span>
        </div>
        <span className="text-base text-slate-700">{t.recommandations.messages[reco.msgKey]}</span>
      </div>
    </div>
  );
};

export const RecommandationsModule = () => {
  const { t } = useI18n();
  const { recommendations } = useBudget();

  const alertes = recommendations.filter((r) => r.level === "alerte");
  const opportunites = recommendations.filter((r) => r.level === "opportunite");
  const infos = recommendations.filter((r) => r.level === "info");

  return (
    <div className="flex flex-col gap-8" data-testid="module-recommandations">
      <header className="flex flex-col gap-2">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
          {t.modules.recommandations}
        </span>
        <h1 className="text-3xl sm:text-4xl tracking-tighter text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
          {t.recommandations.title}
        </h1>
        <p className="text-slate-500 text-base max-w-2xl">{t.recommandations.subtitle}</p>
      </header>

      {alertes.length > 0 && (
        <GlassCard data-testid="reco-section-alertes">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-rose-700" />
            </div>
            <h2 className="text-lg font-medium text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
              {t.recommandations.alertes}
            </h2>
            <span className="ml-auto text-sm text-slate-500">{alertes.length}</span>
          </div>
          <div className="flex flex-col gap-3">
            {alertes.map((r) => (
              <RecoCard key={r.id} reco={r} t={t} />
            ))}
          </div>
        </GlassCard>
      )}

      {opportunites.length > 0 && (
        <GlassCard data-testid="reco-section-opportunites">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-indigo-700" />
            </div>
            <h2 className="text-lg font-medium text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
              {t.recommandations.opportunites}
            </h2>
            <span className="ml-auto text-sm text-slate-500">{opportunites.length}</span>
          </div>
          <div className="flex flex-col gap-3">
            {opportunites.map((r) => (
              <RecoCard key={r.id} reco={r} t={t} />
            ))}
          </div>
        </GlassCard>
      )}

      {infos.length > 0 && (
        <GlassCard data-testid="reco-section-infos">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-700" />
            </div>
            <h2 className="text-lg font-medium text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
              {t.recommandations.infos}
            </h2>
            <span className="ml-auto text-sm text-slate-500">{infos.length}</span>
          </div>
          <div className="flex flex-col gap-3">
            {infos.map((r) => (
              <RecoCard key={r.id} reco={r} t={t} />
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
};
